<template>
    <base-modal size="lg" hide-header hide-footer :allow-overflow="true" @close="$emit('close')">
        <div class="col-12 nopads">
            <div class="col-sm-12 editor-form">
                <h2 class="editor-header">{{ $t('work_assignment.bulk_copy') }}</h2>
                <div class="info-box mt-4 col-12 my-2">
                    <font-awesome-icon icon="info-circle" class="info-circle" />
                    <span>{{ $t('work_assignment.bulk_copy_hint') }}</span>
                </div>

                <div class="row mt-2">
                    <div class="col-12 col-sm-6">
                        <span class="span-title">{{ $t('work_assignment.assignee_company') }}</span>
                        <select v-model="copyCompany" class="form-select form-select-sm"
                            :disabled="!companyOptions || companyOptions.length < 1"
                            @change="onCopyCompanyChange">
                            <option :value="null">{{ $t('work_assignment.no_change') }}</option>
                            <option :value=-1>{{ $t('work_assignment.no_responsible_company') }}</option>
                            <option v-for="c in companyOptions" :key="c.value" :value="c.value">{{ c.name }}</option>
                        </select>
                    </div>
                    <div class="col-12 col-sm-6">
                        <span class="span-title">{{ $t('work_assignment.select_assignee') }}</span>
                        <select v-model="copyPerson" class="form-select form-select-sm" @change="onCopyUserChange">
                            <option :value="null">{{ $t('work_assignment.no_change') }}</option>
                            <option :value=-1>{{ $t('work_assignment.no_responsible_person') }}</option>
                            <option v-for="user in copyUsers" :key="user.id" :value="user.id">{{ user.last_name }}, {{ user.first_name }}</option>
                        </select>
                    </div>
                    <div class="col-12 col-sm-6">
                        <span class="span-title">{{ $t('orders.contract') }}</span>
                        <multiselect v-model="copyContract" :options="contracts" label="name" track-by="id"
                            :placeholder="$t('work_assignment.no_change')" :allow-empty="true" :show-labels="false"
                            @update:modelValue="onCopyContractChange" />
                    </div>
                    <div class="col-12 col-sm-6">
                        <span class="span-title">{{ $t('observations.orders') }}</span>
                        <select v-model="copyOrder" class="form-select form-select-sm"
                            :disabled="!copyContract || copyOrders.length === 0">
                            <option :value="null">{{ $t('work_assignment.no_change') }}</option>
                            <option v-for="order in copyOrders" :key="order.id" :value="order.id">{{ order.id }} - {{ getOrderAndExecutorStr(order) }}</option>
                        </select>
                    </div>
                    <div class="col-12 col-sm-6">
                        <span class="span-title">{{ $t('work_assignment.select_status') }}</span>
                        <select v-model="status" class="form-select form-select-sm">
                            <option :value="null">{{ $t('work_assignment.no_change') }}</option>
                            <option v-for="option in getStatus(false)" :key="option.status" :value="option.status">{{ option.key }}</option>
                        </select>
                    </div>
                    <div class="col-12 col-sm-6 d-flex align-items-center mt-3">
                        <input id="copyAttachments" v-model="copyAttachments" type="checkbox" class="form-check-input me-2" />
                        <label for="copyAttachments" class="span-title mb-0">{{ $t('work_assignment.copy_attachments') }}</label>
                    </div>
                </div>

                <div class="col-sm-12 text-end mt-3 pt-2">
                    <button class="btn btn-secondary form-button" @click.stop="$emit('close')">
                        {{ $t('common.cancel') }}
                    </button>
                    <button :disabled="loading" class="btn btn-success form-button" @click.stop="submitBulkCopy()">
                        {{$t('common.copy') }}
                    </button>
                </div>
            </div>
            <div v-if="loading" id="loader" class="spinner" />
        </div>
    </base-modal>
</template>

<script>
import {restApi} from '../mixins/RestApiMixin'
import {timeUtils} from '../mixins/TimeUtils'
import {workAssignmentHelper} from '../mixins/WorkAssignmentMixin'
import {orderMixin} from "../mixins/OrderMixin.js";
import BaseModal from '../BaseModal'
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import {inject} from "vue";

export default {
    name: 'WorkAssignmentBulkCopy',
    components: {FontAwesomeIcon, BaseModal },
    mixins: [restApi, timeUtils, workAssignmentHelper, orderMixin],
    props: {
        assignments: { type: Array, default: () => [] },
        isObserver: { type: Boolean, default: false }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    emits: ['close', 'closeAndUpdate'],
    data() {
        return {
            loading: 0,
            copyAttachments: false,
            copyCompany: null,
            copyPerson: null,
            copyContract: null,
            copyOrder: null,
            status: null,
            companyOptions: [],  // [{name, value}] — populated from contractCompanyUrl
            copyUsers: [],
            contracts: [],
            copyOrders: []
        }
    },
    mounted() {
        this.fetchContracts()
        this.fetchCompanies()
        this.fetchUsers()
    },
    methods: {
        fetchCompanies() {
            this.companyOptions = []
            this.loading++
            this.restFetch(this.contractorUrl, response => {
                if (response && response.data) {
                    response.data.forEach(company => {
                        this.companyOptions.push({ name: company.name, value: company.id })
                    })
                }
                this.loading--
            })
        },
        fetchUsers() {
            this.loading++
            this.restFetchParams(this.userCompanyUrl, {active: 1}, resp => {
                this.copyUsers = resp?.data ?? []
                this.loading--
            })
        },
        fetchContracts() {
            this.loading++
            this.restFetch(this.contractOpenUrl, response => {
                this.contracts = response?.data ?? []
                this.loading--
            })
        },
        fetchCopyOrders(contractId) {
            this.loading++
            this.restFetchParams(this.orderUrl, { contract: contractId, open: 1 }, resp => {
                this.copyOrders = resp?.data ?? []
                this.loading--
            })
        },
        onCopyUserChange() {
            if (this.copyPerson !== null) {
                this.copyCompany = -1
                this.status = this.ASSIGNED
            } else {
                this.status = null
            }
        },
        onCopyCompanyChange() {
            this.copyPerson = -1
        },
        onCopyContractChange(contract) {
            this.copyContract = contract
            this.copyOrder = null
            this.copyOrders = []
            this.copyCompany = null
            this.copyPerson = null
            this.copyUsers = []
            if (contract) {
                this.fetchCopyOrders(contract.id)
            }
        },
        buildCopyPayload() {
            const payload = { assignments: this.assignments.map(a => a.id), copy_attachments: this.copyAttachments }
            if (this.copyCompany !== null) payload.assigned_company = this.copyCompany
            if (this.copyPerson !== null) payload.assigned_to = this.copyPerson
            if (this.copyContract !== null) payload.contract = this.copyContract.id
            if (this.copyOrder !== null) payload.order = this.copyOrder
            if (this.status !== null) payload.status = this.status
            return payload
        },
        submitBulkCopy() {
            this.validationError = null
            this.loading++
            this.restAdd(this.workAssignmentsBulkCopyUrl, this.buildCopyPayload(), this.onSuccess, this.onFail)
        },
        onSuccess() {
            this.loading--
            this.$emit('closeAndUpdate')
        },
        onFail() {
            this.loading--
            this.showAlert(this.$t('common.alert_update_failed'))
        }
    }
}
</script>

<style>
@import "../../styles/modal.css";
</style>
